import { Property } from '../types';

export const searchProperties = async (term: string, signal?: AbortSignal): Promise<Property[]> => {
  if (!term || term.length < 3) return [];

  const apiUrl = `/api/buscar?q=${encodeURIComponent(term)}&_t=${Date.now()}`;

  try {
    const response = await fetch(apiUrl, { signal });

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error("A rota de API '/api/buscar' não foi encontrada.");
      }

      const contentType = response.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        const errorData = await response.json();
        throw new Error(errorData.details || errorData.error || `Erro ${response.status}`);
      }
      // Em HTTP/2 (usado pela Vercel) o navegador não expõe statusText, então
      // sempre incluímos o código numérico para o erro não ficar em branco.
      const statusLabel = response.statusText ? `${response.status} ${response.statusText}` : `${response.status}`;
      throw new Error(`Erro no servidor (${statusLabel}). Tente novamente em instantes.`);
    }

    const json = await response.json();
    return json.data || [];
  } catch (error) {
    const isAbort = error instanceof DOMException && error.name === 'AbortError';
    if (!isAbort) {
      console.error("Erro na busca de imóveis:", error);
    }
    throw error;
  }
};

/**
 * Exporta os dados diretamente para Excel (.xlsx).
 * A lib xlsx é carregada sob demanda: ela representa ~1/3 do bundle e só é
 * necessária quando o usuário realmente clica em exportar.
 */
export const exportToCSV = async (data: Property[]) => {
  if (!data || data.length === 0) return;

  const XLSX = await import('xlsx');

  const worksheetData = data.map(item => ({
    'Endereço': item.endereco,
    'Complemento': item.complemento || '-',
    'Bairro': item.bairro || '-',
    'Uso do Imóvel': item.descricao_uso || '-',
    'Matrícula': item.matricula || '-',
    'SQL / Contribuinte': item.numero_contribuinte || '-',
    'Valor Transação (R$)': item.valor_transacao,
    'Proporção (%)': item.proporcao_transmitida || 100,
    'Natureza': item.natureza_transacao || '-',
    'Data': item.data_transacao,
    'Área Terreno (m²)': item.area_terreno,
    'Área Construída (m²)': item.area_construida,
    'Valor Financiado (R$)': item.valor_financiado || 0,
    'Tipo Financiamento': item.tipo_financiamento || '-',
    'Cartório': item.cartorio
  }));

  const worksheet = XLSX.utils.json_to_sheet(worksheetData);

  const wscols = [
    { wch: 40 }, // Endereço
    { wch: 15 }, // Complemento
    { wch: 20 }, // Bairro
    { wch: 20 }, // Uso
    { wch: 12 }, // Matrícula
    { wch: 18 }, // SQL
    { wch: 18 }, // Valor
    { wch: 12 }, // Proporção
    { wch: 20 }, // Natureza
    { wch: 12 }, // Data
    { wch: 15 }, // Terreno
    { wch: 15 }, // Construída
    { wch: 18 }, // Financiado
    { wch: 20 }, // Tipo
    { wch: 25 }, // Cartório
  ];
  worksheet['!cols'] = wscols;

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Transações");

  const fileName = `rdk_imoveis_export_${new Date().toISOString().split('T')[0]}.xlsx`;
  XLSX.writeFile(workbook, fileName);
};